import { Routes, Route, Navigate } from 'react-router-dom';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from 'react-query';
import { useAuth } from '@unbooking/ui-auth';
import { CallbackPage, CMSPage, EmptyPage, LoginPage, NoAccessPage } from '@pages';
import { FacilityProvider, GlobalProvider, RepositoryProvider, useGlobalContext } from '@context';
import routes from './routes';
import { Footer, Header, Toast } from '@components';
import { handleError } from '@common/utils';
import { WaffleFlags } from '@common/interfaces';
import { useAppType } from '@common/hooks';
import { AppType, URLS } from '@common/constants';
import { useLocation } from 'react-router';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 2, // Note: hook for getting data in token refresh process
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query?.meta?.disableGlobalErrorHandler) return;
      handleError(error);
    },
  }),
  mutationCache: new MutationCache({
    onError: (err) => handleError(err),
  }),
});

const restrictedAdminRoutesForManager = [URLS.ADMIN_AREA_FACILITIES, URLS.ADMIN_AREA_CUSTOMER_AREAS];

const Pages = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const {
    waffles,
    user: { customer_area_location, is_do_manager, is_do_superuser },
    isUserSuccess,
  } = useGlobalContext();
  const appType = useAppType();

  // TO DO: Deprecated variant, delete in future
  // useEffect(() => {
  //   if (isUserSuccess && appType === AppType.CUSTOMER_AREA && !customer_area_location) {
  //     navigate(URLS.FORBIDDEN);
  //   }

  //   if (isUserSuccess && appType === AppType.ADMIN_AREA && !is_do_manager && !is_do_superuser) {
  //     navigate(URLS.FORBIDDEN);
  //   }
  // }, [isUserSuccess, customer_area_location]);

  return (
    <>
      {isAuthenticated ? <Header /> : null}
      <Routes location={location} key={location.pathname}>
        {isAuthenticated ? (
          <>
            <Route path='/' element={<Navigate to='/home' />} />
            {isUserSuccess ? (
              routes.map(({ Component, path, waffle }, i) => {
                // Hide route if waffle flag is disabled
                const pageWaffle = waffles.flags[waffle as keyof WaffleFlags];
                const isWaffleVisible = !pageWaffle || pageWaffle.is_active;

                if (!isWaffleVisible) {
                  return <Route key={i} path={path} element={<NoAccessPage />} />;
                }

                // CUSTOMER_AREA: block access if user has no customer area location
                if (appType === AppType.CUSTOMER_AREA && !customer_area_location) {
                  return <Route key={i} path={path} element={<NoAccessPage />} />;
                }

                // ADMIN_AREA: block all admin pages for non-manager and non-superuser
                if (appType === AppType.ADMIN_AREA && !is_do_manager && !is_do_superuser) {
                  return <Route key={i} path={path} element={<NoAccessPage />} />;
                }

                // ADMIN_AREA: manager (not superuser) blocked from restricted routes
                if (
                  appType === AppType.ADMIN_AREA &&
                  is_do_manager &&
                  !is_do_superuser &&
                  restrictedAdminRoutesForManager.includes(path)
                ) {
                  return <Route key={i} path={path} element={<NoAccessPage />} />;
                }

                // Default: render allowed page
                return <Route key={i} path={path} element={<Component />} />;
              })
            ) : (
              <Route path='*' element={<EmptyPage />} />
            )}
            <Route path='callback/*' element={<CallbackPage />} />
            <Route path='*' element={<CMSPage />} />
          </>
        ) : (
          <>
            <Route index element={<LoginPage />} />
            <Route path='callback/*' element={<CallbackPage />} />
            <Route path='*' element={<Navigate to='/' />} />
          </>
        )}
      </Routes>
      <Toast />
      {isAuthenticated ? <Footer /> : null}
    </>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RepositoryProvider>
        <FacilityProvider>
          <GlobalProvider>
            <Pages />
          </GlobalProvider>
        </FacilityProvider>
      </RepositoryProvider>
    </QueryClientProvider>
  );
}

export default App;
